import React, { useRef, useState } from "react";
import ListItem from "@mui/material/ListItem";
import Checkbox from "@mui/material/Checkbox";
import { makeStyles } from "tss-react/mui";
import InputBase from "@mui/material/InputBase";
import * as PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const useStyles = makeStyles()((theme) => ({
  listItem: {
    paddingRight: "64px",
    // maxHeight: '60px',
    // transition: 'all 500ms',
    overflow: "hidden",
  },

  checkedItem: {
    textDecoration: "line-through",
  },

  deleteButton: {
    position: "absolute",
    right: "20px",
    padding: "5px 4px",
    color: theme.palette.error.main,
  },

  actions: {
    width: "64px",
    right: "6px;",
  },

  disabled: {
    color: theme.palette.text.primary,
  },

  dragHandle: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },

  dropIndicatorBase: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      backgroundColor: theme.palette.primary.main,
      zIndex: 10,
      opacity: 0,
      transition: "opacity 0.2s ease-in-out",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "2px",
      backgroundColor: theme.palette.primary.main,
      zIndex: 10,
      opacity: 0,
      transition: "opacity 0.2s ease-in-out",
    },
  },

  dropIndicatorTop: {
    "&::before": {
      opacity: 1,
    },
  },

  dropIndicatorBottom: {
    "&::after": {
      opacity: 1,
    },
  },
}));

ShoppingListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    checked: PropTypes.bool,
  }).isRequired,
  setItem: PropTypes.func.isRequired,
  handleCheck: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  addItem: PropTypes.func,
  autoFocus: PropTypes.bool,
  editMode: PropTypes.bool,
  isDraggable: PropTypes.bool,
  dragHandlers: PropTypes.object,
  dragIndex: PropTypes.number,
  isDragged: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  dropPosition: PropTypes.oneOf(["top", "bottom", null]),
};

ShoppingListItem.defaultProps = {
  addItem: null,
  autoFocus: false,
  editMode: false,
  isDraggable: false,
  dragHandlers: null,
  dragIndex: undefined,
  isDragged: false,
  isDropTarget: false,
  dropPosition: null,
};

export default function ShoppingListItem(props: any) {
  const { classes, cx } = useStyles();
  const liRef = useRef(null);
  const [checked, setChecked] = useState(!!props.item.checked);

  function onCheck() {
    if (!props.editMode) {
      setChecked(!checked);
      props.handleCheck();
    }
  }

  function onTextChange(text: any) {
    props.setItem({
      ...props.item,
      text,
    });
  }

  function keyPressed(e: any) {
    if (e.charCode === 13 && props.addItem) {
      props.addItem();
      e.preventDefault();
    }
  }

  return (
    <ListItem
      ref={liRef}
      className={cx(classes.listItem, {
        [classes.dropIndicatorBase]: props.isDraggable,
        [classes.dropIndicatorTop]:
          props.isDropTarget && props.dropPosition === "top",
        [classes.dropIndicatorBottom]:
          props.isDropTarget && props.dropPosition === "bottom",
      })}
      onClick={() => onCheck()}
      data-drag-index={props.dragIndex}
      style={{ opacity: props.isDragged ? 0.5 : 1 }}
      onDragOver={props.dragHandlers?.handleDragOver}
      onDrop={props.dragHandlers?.handleDrop}
    >
      {props.isDraggable && (
        <div
          draggable
          onDragStart={props.dragHandlers?.handleDragStart}
          onDragEnd={props.dragHandlers?.handleDragEnd}
          onTouchStart={(e) => {
            e.stopPropagation(); // Prevent event from bubbling to ListItem
            props.dragHandlers?.handleTouchStart(e);
          }}
          style={{ display: "flex", alignItems: "center", touchAction: "none" }}
        >
          <DragIndicatorIcon className={classes.dragHandle} />
        </div>
      )}
      {!props.editMode && (
        <Checkbox
          checked={checked || false}
          onChange={() => onCheck()}
          inputProps={{ "aria-label": "list-item-checkbox" }}
        />
      )}
      <InputBase
        autoFocus={props.autoFocus}
        className={cx({ [classes.checkedItem]: checked })}
        classes={{ disabled: classes.disabled }}
        fullWidth
        value={props.item.text}
        onChange={(e: any) => onTextChange(e.target.value)}
        onKeyPress={keyPressed}
        disabled={!props.editMode}
      />
      {props.editMode && (
        <IconButton
          className={classes.deleteButton}
          onClick={() => props.removeItem()}
          aria-label="remove-item"
        >
          <DeleteIcon />
        </IconButton>
      )}
    </ListItem>
  );
}
